import { createSelector } from 'reselect';
import moment from 'moment';
import {
  dateStringIntoDate,
  getDatesSequence,
  mapCompanyBranchesToSelect,
  mapEmployeesToCalendarPage,
} from 'modules/SalaryModule/Calendar/helpers';
import { ISO_DATE_FORMAT } from 'const';
import { YEAR_TABLE_DATA_TEMPLATE } from 'const/Calendar';
import {
  selectCompanyBranches,
  selectEmployees,
  selectAllEmployees,
} from 'modules/SalaryModule/Employees/selectors';
import { LEAVES_TYPES } from 'const/translations/SalaryCalendar';
const { LEAVE_ABSENCE, LEAVE_VACATION } = LEAVES_TYPES;

export const selectMonthYearToggleState = (state) =>
  state.calendarReducer?.monthYearToggle;

export const selectCalendarEmployeeState = (state) =>
  state.calendarReducer?.calendarEmployee;

export const selectCalendarAbsencesStatus = (state) =>
  state.calendarReducer?.absencesStatus;

export const selectCalendarCompanyBranchesForSelect = createSelector(
  [selectCompanyBranches],
  (branches) => mapCompanyBranchesToSelect(branches)
);

export const selectCalendarWithAbsencesOnly = (state) =>
  state.calendarReducer?.withAbsencesOnly || false;

export const selectCalendarBranchFilter = (state) =>
  state.calendarReducer?.branchFilter || '';

export const selectCalendarDateFilter = (state) =>
  moment(state.calendarReducer?.dateFilter) || moment().startOf('month');

export const selectCalendarYear = (state) =>
  moment(state.calendarReducer?.dateFilter)?.year() || 0;

export const selectHolidaysUpdating = (state) =>
  state.calendarReducer?.areHolidaysUpdating || false;

export const selectYearsWithoutHolidays = (state) =>
  state.calendarReducer?.yearsWithoutHolidays || [];

export const selectReasonsByType = (state, type) =>
  state?.calendarReducer?.absencesTypes?.data?.reasons?.filter(
    (el) => el.type === type
  );

export const selectAbsencesTypes = (state) =>
  selectReasonsByType(state, LEAVE_ABSENCE);

export const selectVacationTypes = (state) =>
  selectReasonsByType(state, LEAVE_VACATION);

export const selectCalendarPagination = (state) => ({
  currentPage: state.calendarReducer.currentPage,
  pages: state.calendarReducer.pages,
  limit: state.calendarReducer.limit,
  total: state.calendarReducer.total,
});

export const selectDatesArrayForCurrentMonth = createSelector(
  [selectCalendarDateFilter],
  (monthFilter) => {
    const startDateIso = moment(monthFilter)
      .startOf('month')
      .format(ISO_DATE_FORMAT);
    const endDateIso = moment(startDateIso)
      .endOf('month')
      .format(ISO_DATE_FORMAT);
    return getDatesSequence(startDateIso, endDateIso);
  }
);

export const selectDatesArrayForCurrentYear = createSelector(
  [selectCalendarDateFilter],
  (monthFilter) => {
    const startDateIso = moment(monthFilter)
      .startOf('year')
      .format(ISO_DATE_FORMAT);
    const endDateIso = moment(startDateIso)
      .endOf('year')
      .format(ISO_DATE_FORMAT);
    return getDatesSequence(startDateIso, endDateIso);
  }
);

export const selectCalendarEmployees = createSelector(
  [selectAllEmployees],
  (employees) => mapEmployeesToCalendarPage(employees)
);

export const selectCalendarAbsences = (state) =>
  state.calendarReducer?.absences || [];

export const selectAllAbsencesFlat = createSelector(
  [selectEmployees, selectCalendarAbsences],
  (employees, calendarAbsences) => {
    const employeeAbsences = employees.flatMap(
      (employee) => employee.absences || []
    );

    // const validCalendarAbsences = calendarAbsences.filter((el) => el.reason);

    return [...employeeAbsences, ...calendarAbsences];
  }
);

export const selectEmployeesWithAbsences = createSelector(
  [selectEmployees, selectCalendarAbsences],
  (employees, absences) =>
    employees.map((employee) => {
      const absencesByDate = {};

      const newAbsences = absences.filter(
        (el) => el.reason && el.employeeId === employee.employeeid
      );

      employee.absences?.forEach((absence) => {
        const dates = getDatesSequence(absence.start, absence.end);

        dates.forEach((date) => {
          absencesByDate[date] = {
            employeeId: employee.employeeid,
            start: absence.start,
            end: absence.end,
            id: absence.id,
            type: absence.reason?.type,
            reason: absence.reason,
            isCrossingAnotherAbsence: !!absencesByDate[date],
            startTimeOfTheDay: absence.startTimeOfTheDay,
            endTimeOfTheDay: absence.endTimeOfTheDay,
          };
        });
      });

      newAbsences.forEach((absence) => {
        const dates = getDatesSequence(absence.start, absence.end);

        dates.forEach((date) => {
          absencesByDate[date] = {
            employeeId: employee.employeeid,
            start: absence.start,
            end: absence.end,
            id: absence.id,
            type: absence.reason?.type,
            reason: absence.reason,
            isCrossingAnotherAbsence: !!absencesByDate[date],
            startTimeOfTheDay: absence.startTimeOfTheDay,
            endTimeOfTheDay: absence.endTimeOfTheDay,
          };
        });
      });

      return {
        ...employee,
        absences: absencesByDate,
      };
    })
);

export const selectEmployeesWithAbsencesOnly = createSelector(
  [selectCalendarDateFilter, selectEmployeesWithAbsences],
  (monthFilter, filteredEmployees) =>
    filteredEmployees.filter(({ absences }) =>
      Object.keys(absences).some((absenceDate) =>
        moment(dateStringIntoDate(absenceDate)).isSame(monthFilter, 'month')
      )
    )
);

export const selectYearCalendarEmployee = createSelector(
  [selectCalendarEmployeeState, selectEmployees, selectAllAbsencesFlat],
  (calendarEmployee, employees, allAbsences) => {
    const employeeData = employees.find(
      (e) => e.employeeid === calendarEmployee
    );

    if (!employeeData) {
      return JSON.parse(JSON.stringify(YEAR_TABLE_DATA_TEMPLATE));
    }

    const employeeAbsences = allAbsences.filter(
      (absence) => absence.employeeId === calendarEmployee
    );

    const tableData = JSON.parse(JSON.stringify(YEAR_TABLE_DATA_TEMPLATE));

    if (employeeAbsences && Array.isArray(employeeAbsences)) {
      employeeAbsences.forEach((absence) => {
        const startDate = new Date(absence.start);
        const endDate = absence.end
          ? new Date(absence.end)
          : new Date(absence.start);

        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const day = currentDate.getUTCDate();
          const month = currentDate.getUTCMonth() + 1;

          const tableRow = tableData.find((e) => e.key === month);
          if (tableRow) {
            tableRow[`day-${day}`] = {
              employeeid: employeeData.employeeid,
              firstname1: employeeData.firstname1 || '',
              lastname: employeeData.lastname || '',
              siret: employeeData.siret || '',
              scheduleId: employeeData.scheduleId || '',
              startdate: employeeData.startdate || '',
              enddate: employeeData.enddate || '',
              absences: { ...absence },
              dateString: `${day}/${month}`,
              isLoading: !absence.id,
            };
          }
          currentDate.setUTCDate(currentDate.getUTCDate() + 1);
        }
      });
    }

    return tableData;
  }
);
